/**
 * Timeline Component
 *
 * A clean, organized timeline visualization displaying all events related to an entity (Task, Client, or Resource).
 * Events are grouped by date in reverse chronological order (newest first).
 *
 * Features:
 * - 📅 Clean date-based organization with subtle separators
 * - 🔍 Filterable event types (status, schedule, planning, reminders, history) - shows all by default
 * - 🎯 Consistent card layout with color-coded icons
 * - 👥 Visual representation of assigned resources and notified users
 * - ✨ Interactive event cards with modal details
 * - 🌐 Entity-aware: adapts to Task, Client, or Resource contexts
 *
 * Filter Behavior:
 * - Default: All event types shown (no active filters)
 * - Active filters: Only selected types displayed
 * - Deselecting all filters returns to showing everything
 *
 * Entity-Specific Behavior:
 * - Task: Shows status, schedule, planning, and reminders
 * - Client/Resource: Shows history (task events) and reminders
 */

import React from 'react';
import { TimelineProvider, useTimelineContext } from './context/TimelineContext';
import { TimelineHeader } from './components/TimelineHeader';
import { TimelineEventList } from './components/TimelineEventList';
import { TimelineFooter } from './components/TimelineFooter';
import ReminderDetailModal from './editors/ReminderDetailModal';
import StatusChangeModal from './editors/StatusChangeModal';
import PlanningDetailModal from './editors/PlanningDetailModal';
import { ResourceDetailModal } from './editors/ResourceDetailModal';
import HistoryDetailModal from './editors/HistoryDetailModal';
import ReminderEditModal from './editors/ReminderEditModal';
import ScheduleEditModal from './editors/ScheduleEditModal';
import PlanningEditModal from './editors/PlanningEditModal';
import { PERSISTENCE_STATE_KEYS } from '@/other/constants/PersistenceStateKeys';

interface TimelineContentProps {
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Timeline content component - renders the actual timeline UI
 */
const TimelineContent: React.FC<TimelineContentProps> = ({ onOpenChange }) => {
  const {
    record,
    entityType,
    reminderModal,
    statusModal,
    planningModal,
    resourceModal,
    historyModal,
    closeReminderModal,
    closeStatusModal,
    closePlanningModal,
    closeResourceModal,
    closeHistoryModal,
    reminderEditModal,
    setReminderEditModal,
    scheduleEditModal,
    setScheduleEditModal,
    planningEditModal,
    setPlanningEditModal,
    timelineData,
    selectedMode,
    config,
    isTimelineOpen,
    setIsTimelineOpen: setIsTimelineOpenContext,
    project
  } = useTimelineContext();

  // Get notify users for reminder modal
  const notifyUsers = 'reminderNotifyUsers' in record ? (record.reminderNotifyUsers || []) : [];

  // Check if there are any events based on selected mode
  const hasEvents = React.useMemo(() => {
    if (selectedMode === 'status' && config.showStatusHistory) {
      return timelineData.statusChanges.length > 0;
    } else if (selectedMode === 'schedule' && config.showSchedule) {
      return timelineData.scheduleDates.length > 0;
    } else if (selectedMode === 'planning' && config.showPlanning) {
      return timelineData.planningDates.length > 0;
    } else if (selectedMode === 'history' && config.showHistory) {
      return timelineData.historyDates.length > 0;
    } else if (selectedMode === 'reminders') {
      return timelineData.reminderDates.length > 0;
    }
    return false;
  }, [timelineData, selectedMode, config]);

  // Wrapper to update context state and notify parent
  const setIsTimelineOpen = React.useCallback((isOpen: boolean) => {
    setIsTimelineOpenContext(isOpen);
  }, [setIsTimelineOpenContext]);

  // Notify parent whenever timeline open state changes (from persistence load or user interaction)
  React.useEffect(() => {
    onOpenChange?.(isTimelineOpen);
  }, [isTimelineOpen, onOpenChange]);

  // Auto-open only when events are first added (transition from empty to having events)
  React.useEffect(() => {
    if (project) {
      const timelineExpandedState = project?.userPreferencesStore?.getSetting(PERSISTENCE_STATE_KEYS.timelineExpandedState);
      if (timelineExpandedState === undefined && hasEvents) {
        setIsTimelineOpen(true);
      }
    }
  }, [hasEvents, setIsTimelineOpen, project]);

  // Allow user control over expanded state
  const shouldShowExpanded = isTimelineOpen;

  return (
    <>
      <div className="border-r flex flex-col min-h-0 flex-1">
        <TimelineHeader
          isTimelineOpen={shouldShowExpanded}
          setIsTimelineOpen={setIsTimelineOpen}
          hasEvents={hasEvents}
        />
        {shouldShowExpanded && (
          <>
            <TimelineEventList />
            <TimelineFooter />
          </>
        )}
      </div>

      {/* Detail Modals */}
      {resourceModal.data && (
        <ResourceDetailModal
          open={resourceModal.isOpen}
          onOpenChange={closeResourceModal}
          resources={resourceModal.data}
        />
      )}
      {planningModal.data && (
        <PlanningDetailModal
          open={planningModal.isOpen}
          onOpenChange={closePlanningModal}
          planningEvents={planningModal.data}
        />
      )}
      {statusModal.data && (
        <StatusChangeModal
          open={statusModal.isOpen}
          onOpenChange={closeStatusModal}
          statusChanges={statusModal.data}
        />
      )}
      {reminderModal.data && (
        <ReminderDetailModal
          open={reminderModal.isOpen}
          onOpenChange={closeReminderModal}
          reminder={reminderModal.data}
          notifyUsers={notifyUsers}
        />
      )}
      {historyModal.data && (
        <HistoryDetailModal
          open={historyModal.isOpen}
          onOpenChange={closeHistoryModal}
          historyEvent={historyModal.data}
        />
      )}

      {/* Edit Modals */}
      <ReminderEditModal
        open={reminderEditModal.isOpen}
        onOpenChange={(isOpen) => setReminderEditModal({ ...reminderEditModal, isOpen })}
        mode={reminderEditModal.mode}
        reminderId={reminderEditModal.reminderId}
        entityId={record?.id as string}
        entityType={entityType}
      />
      <ScheduleEditModal
        open={scheduleEditModal.isOpen}
        onOpenChange={(isOpen) => setScheduleEditModal({ ...scheduleEditModal, isOpen })}
        mode={scheduleEditModal.mode}
        eventId={scheduleEditModal.eventId}
        taskId={record?.id as string}
      />
      <PlanningEditModal
        open={planningEditModal.isOpen}
        onOpenChange={(isOpen) => setPlanningEditModal({ ...planningEditModal, isOpen })}
        mode={planningEditModal.mode}
        planningId={planningEditModal.planningId}
        taskId={record?.id as string}
        defaultFormValues={planningEditModal.defaultFormValues}
      />
    </>
  );
};

interface TimelineProps {
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Main Timeline component - provides context and renders content
 */
export const Timeline: React.FC<TimelineProps> = ({ onOpenChange }) => {
  return (
    <TimelineProvider>
      <TimelineContent onOpenChange={onOpenChange} />
    </TimelineProvider>
  );
};
